/**
 * Basemap configuration, kept free of React and deck.gl so it can be unit-tested.
 */

/**
 * The Google Maps API key, or null when none is configured.
 *
 * Returning null rather than throwing is the point: without a key the app still has a
 * working demand model and a useful ranked list, so a missing key degrades the map
 * rather than taking down the page. See the no-key branch in App.tsx.
 *
 * Vite inlines `import.meta.env` values at build time, so this is baked into the bundle.
 * That is fine and unavoidable for a browser map key — it is meant to be public. Protect
 * it the way Google intends: restrict it by HTTP referrer and to the Maps JavaScript API
 * in the Cloud console. An unrestricted key on a public page is the actual risk.
 */
export function resolveApiKey(env: Record<string, unknown> = import.meta.env): string | null {
  const raw = env.VITE_GOOGLE_MAPS_API_KEY;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  // Treat the placeholder from .env.example as absent, so copying the file without
  // editing it produces the friendly setup notice rather than a Google auth error.
  if (!trimmed || trimmed === 'your-api-key-here') return null;
  return trimmed;
}

/** Convert the bundle's [south, west, north, east] into Google's bounds literal. */
export function bboxToBounds(
  bbox: readonly [number, number, number, number],
): google.maps.LatLngBoundsLiteral {
  const [south, west, north, east] = bbox;
  return { south, west, north, east };
}

/**
 * Map options that are about doing this job on a bike, rather than about cartography.
 */
export const MAP_OPTIONS = {
  /** One-finger pan. The default demands two fingers, which is unusable one-handed. */
  gestureHandling: 'greedy',
  /**
   * Google's own POI pins would otherwise swallow taps and open their info windows,
   * competing with cell selection — which is the only thing tapping should do here.
   */
  clickableIcons: false,
  disableDefaultUI: true,
  zoomControl: false,
  keyboardShortcuts: false,
  /** Buildings are worth reaching; Google renders them from roughly z16. */
  maxZoom: 19,
  minZoom: 11,
  tilt: 0,
} as const satisfies Partial<google.maps.MapOptions>;

export const LOADER_OPTIONS = {
  version: 'weekly',
  /** English labels, per the chosen setup. Google renders one language, not two. */
  language: 'en',
  /** Biases geocoding and rendering conventions to Israel. */
  region: 'IL',
} as const;
